import { formatInZone, isoToZonedLocal, resolveTimeZone } from '@auctisync/shared';

/**
 * How the sale header writes a preview window.
 *
 * Extracted from the component because this repo tests LOGIC, not renders —
 * `apps/web` has no component renderer wired up, and every other web test here
 * is a `.test.ts` over a pure function. The branching is real (same-day collapse
 * and two one-sided forms), so it is worth a guard even though it is short.
 *
 * 🔴 EVERY TIME HERE IS THE SALE'S, NAMED — NOT THE READER'S. It used to be the
 * reader's, via `toLocaleString(undefined, …)`, which is the browser default and
 * is wrong for an auction. Jim, 2026-09-18: times *"should always show the
 * auction's local time"*. A preview that opens at 7am opens at 7am at the
 * warehouse; showing a bidder in another state their own 5am is a different
 * fact from the one the house published.
 *
 * ⚠️ THE ZONE MUST BE NAMED WHEREVER A TIME IS SHOWN. A sale time printed
 * without its zone looks local and is not, which is strictly worse than showing
 * the reader's own — confidently wrong rather than merely unhelpful.
 *
 * The LOCALE still follows the reader. How a date is spelled is a reading
 * preference; which instant it refers to is a fact about the sale. Only the
 * second belongs to the auction.
 */

/** A date and time, in the sale's zone, with the zone named. */
export function dateTime(iso: string, timeZone: string, withZone = true): string {
  return formatInZone(iso, timeZone, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    withZone,
  });
}

/** Just the clock part, for the far end of a window that starts the same day. */
export function timeOnly(iso: string, timeZone: string): string {
  return formatInZone(iso, timeZone, { hour: 'numeric', minute: '2-digit', withZone: true });
}

/**
 * Same calendar day IN THE SALE'S ZONE.
 *
 * 🔴 THE ZONE CHANGES THE ANSWER, not just the wording. A preview running
 * 7am–4pm in Chicago is one day there and can straddle midnight for a reader in
 * Tokyo. Collapsing on the reader's calendar would print "Wed 7:00 AM – 4:00 PM"
 * for a window the house thinks of as a single day while the dates shown either
 * side disagreed with it.
 */
export function sameDay(a: string, b: string, timeZone: string): boolean {
  const tz = resolveTimeZone(timeZone);
  const dayOf = (iso: string) => isoToZonedLocal(iso, tz).slice(0, 10);
  const first = dayOf(a);
  return first !== '' && first === dayOf(b);
}

/**
 * "Wed 9 Sep, 7:00 AM – 4:00 PM CDT" for a one-day preview, both dates in full
 * when it spans several, and a one-sided form when the house published only one
 * end.
 *
 * A single-day preview is the common case, and printing the date at both ends
 * reads as two separate events rather than one window. The zone is named ONCE,
 * at the end — both halves are in the same zone by definition, and repeating it
 * reads as though they might not be.
 */
export function previewLabel(
  startsAt: string | null,
  endsAt: string | null,
  timeZone: string,
): string | null {
  const tz = resolveTimeZone(timeZone);
  if (startsAt !== null && endsAt !== null) {
    return sameDay(startsAt, endsAt, tz)
      ? `${dateTime(startsAt, tz, false)} – ${timeOnly(endsAt, tz)}`
      : `${dateTime(startsAt, tz, false)} – ${dateTime(endsAt, tz)}`;
  }
  if (startsAt !== null) return `from ${dateTime(startsAt, tz)}`;
  if (endsAt !== null) return `until ${dateTime(endsAt, tz)}`;
  // `previewWindow` in @auctisync/api-client already returns null in this case,
  // so the component never asks. Guarded anyway rather than printing "until
  // Invalid Date" if a future caller is less careful.
  return null;
}
